// ============================================================================
// Live trackers for the OPEN period (CW-N Phase 2a — Jack's change request).
//
// Left card  = current month, No-Miss.
// Right card = current quarter, Compliment.
//
// Both read v_incentive_live_tracker, which is ASSIGNMENT grain: the open
// period has no incentive_payouts rows yet, so no other view can describe it.
// Nothing is ever paid from the open period. Both cards say so, in words,
// because a dollar figure on screen otherwise reads as money owed.
//
// The Compliment card is expected to be entirely `ineligible` right now — no
// crew sizes are on record, so no gift-card amount can be computed. That is
// the true state of the data, not an error, and it is rendered as such.
// ============================================================================

import { fmtCurrency } from '../../shared/format';
import {
  type LiveTrackerRow,
  type TrackerState,
} from './hooks/use-incentive-data';
import {
  facilityWithSub,
  formatPeriodLong,
  formatPeriodRange,
  providerLabel,
} from './format';

const STATE_PILL: Record<TrackerState, { label: string; cls: string }> = {
  on_track: { label: 'On track', cls: 'bg-rag-green/15 text-rag-green' },
  qualified: { label: 'Qualified', cls: 'bg-rag-green/15 text-rag-green' },
  not_qualified: { label: 'Not qualified', cls: 'bg-st-danger-bg text-st-danger-fg' },
  not_yet: { label: 'Not yet', cls: 'bg-st-warn-bg text-st-warn-fg' },
  ineligible: {
    label: 'Ineligible',
    cls: 'bg-tcs-surface-strong text-tcs-text-secondary',
  },
};

export function LiveTrackers({
  rows,
  isLoading,
  error,
}: {
  rows: LiveTrackerRow[];
  isLoading: boolean;
  error: string | null;
}) {
  const noMiss = rows.filter((r) => r.payout_type === 'no_misses_monthly');
  const compliment = rows.filter((r) => r.payout_type === 'compliment_quarterly');

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <TrackerCard
        title="This month · Scope Performance"
        rows={noMiss}
        isLoading={isLoading}
        error={error}
        positive="on_track"
        emptyBody="No Scope Performance assignments are active this month."
      />
      <TrackerCard
        title="This quarter · Compliment"
        rows={compliment}
        isLoading={isLoading}
        error={error}
        positive="qualified"
        emptyBody="No Compliment assignments are active this quarter."
      />
    </div>
  );
}

function TrackerCard({
  title,
  rows,
  isLoading,
  error,
  positive,
  emptyBody,
}: {
  title: string;
  rows: LiveTrackerRow[];
  isLoading: boolean;
  error: string | null;
  positive: 'on_track' | 'qualified';
  emptyBody: string;
}) {
  const first = rows[0];
  const good = rows.filter((r) => r.tracker_state === positive);
  const middle = rows.filter(
    (r) => r.tracker_state === 'not_qualified' || r.tracker_state === 'not_yet',
  );
  const ineligible = rows.filter((r) => r.tracker_state === 'ineligible');
  const potential = good.reduce((s, r) => s + Number(r.potential_amount || 0), 0);

  const heading = first
    ? formatPeriodLong(first.period)
    : title.includes('quarter')
      ? 'This quarter'
      : 'This month';

  return (
    <section className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div>
          <h3 className="text-sm font-semibold text-tcs-text">{heading}</h3>
          <p className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
            {title}
            {first
              ? ` · ${formatPeriodRange(first.period_start, first.period_end)}`
              : ''}
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold tabular-nums text-tcs-text">
            {fmtCurrency(potential)}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
            potential
          </div>
        </div>
      </div>

      {/* Jack's rule: a dollar figure on a live period must never read as
          money owed. Say it in words, on both cards, every time. */}
      <p className="mt-2 rounded-md bg-tcs-bg-muted px-2 py-1.5 text-xs text-tcs-text-secondary">
        Nothing is paid from the current period — this is visibility only.
      </p>

      {error ? (
        <p className="mt-3 text-xs text-st-danger-fg">{error}</p>
      ) : isLoading ? (
        <p className="mt-3 text-sm text-tcs-text-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-sm text-tcs-text-muted">{emptyBody}</p>
      ) : (
        <>
          <p className="mt-2 text-xs text-tcs-text-secondary">
            <span className="font-semibold text-tcs-text">{good.length}</span>{' '}
            {positive === 'on_track' ? 'on track' : 'qualified'} ·{' '}
            <span className="font-semibold text-tcs-text">{middle.length}</span>{' '}
            {positive === 'on_track' ? 'not qualified' : 'not yet'} ·{' '}
            <span className="font-semibold text-tcs-text">
              {ineligible.length}
            </span>{' '}
            ineligible
          </p>

          <ul className="mt-2 max-h-72 divide-y divide-tcs-border overflow-y-auto">
            {[...good, ...middle, ...ineligible].map((r) => (
              <TrackerRow key={`${r.payout_type}-${r.assignment_id}`} row={r} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function TrackerRow({ row }: { row: LiveTrackerRow }) {
  const pill = STATE_PILL[row.tracker_state];
  return (
    <li className="flex items-start justify-between gap-2 py-1.5">
      <div className="min-w-0">
        <div className="truncate text-xs font-medium text-tcs-text">
          {facilityWithSub(row.facility_name, row)}
        </div>
        <div className="text-[11px] text-tcs-text-muted">
          {providerLabel(row)} · {row.account_manager_name ?? 'No AM'}
        </div>
        {/* Written as display text by the view — render verbatim. */}
        {row.reason && (
          <div className="mt-0.5 text-[11px] text-tcs-text-secondary">
            {row.reason}
          </div>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${pill.cls}`}
        >
          {pill.label}
        </span>
        <span className="text-[11px] tabular-nums text-tcs-text-secondary">
          {fmtCurrency(Number(row.potential_amount || 0))}
        </span>
      </div>
    </li>
  );
}
